import React from "react";
import { Facebook, Instagram, MapPin, Phone, Mail } from "lucide-react";

export default function Footer({ onDownloadCatalog }) {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0a1a17] text-white border-t border-[#c99658]/15"
    >
      <div className="container-vlx py-20 md:py-24">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <img
              src="/viewlux/logo.png"
              alt="ViewLux Windows and Doors"
              className="h-28 md:h-32 w-auto"
            />
            <div className="mt-4 inline-flex items-center gap-3 text-[10px] tracking-[0.5em] uppercase font-raleway text-[#e2b880]">
              <span className="w-6 h-px bg-[#c99658]" />
              Colorado · Rocky Mountain Region
            </div>
            <p className="mt-6 text-white/65 text-[14.5px] leading-[1.9] max-w-sm font-nunito">
              ViewLux Colorado delivers premium thermally broken aluminum
              window and door systems for the Rocky Mountain region — refined
              sightlines, insulated glass options for year-round indoor
              comfort, and uncompromising performance.
            </p>
            <a
              data-testid="footer-main-site"
              href="https://viewluxwindows.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.4em] uppercase font-raleway text-[#e2b880] hover:text-white transition-colors border-b border-[#c99658]/40 pb-1"
            >
              More information at viewluxwindows.com
              <span aria-hidden="true">↗</span>
            </a>
            <div className="mt-10 space-y-3">
              <InfoRow icon={Phone} text="720.770.3795" href="tel:7207703795" />
              <InfoRow
                icon={Mail}
                text="sales@viewluxcolorado.com"
                href="mailto:sales@viewluxcolorado.com"
              />
              <InfoRow
                icon={MapPin}
                text="8245 I-25 Frontage Rd, Unit 2 · Frederick, CO 80516"
                href="https://maps.google.com/?q=8245+I-25+Frontage+Rd+Unit+2+Frederick+CO+80516"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <h3 className="font-raleway text-[10.5px] tracking-[0.45em] uppercase text-[#e2b880] mb-6">
              Explore
            </h3>
            <ul className="space-y-3.5 text-[14.5px] font-nunito">
              {[
                ["The Difference", "/#about"],
                ["The Collection", "/#collection"],
                ["Commercial", "/commercial"],
                ["Why Aluminum", "/#aluminum"],
                ["Gallery", "/#gallery"],
                ["Journal", "/blog"],
              ].map(([l, h]) => (
                <li key={l}>
                  <a
                    href={h}
                    className="text-white/80 hover:text-[#e2b880] transition-colors"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h3 className="font-raleway text-[10.5px] tracking-[0.45em] uppercase text-[#e2b880] mb-6">
              Support
            </h3>
            <ul className="space-y-3.5 text-[14.5px] font-nunito">
              <li>
                <button
                  data-testid="footer-cta-catalog"
                  onClick={onDownloadCatalog}
                  className="text-[#e2b880] hover:text-white transition-colors text-left underline-offset-4 hover:underline"
                >
                  Download Catalog 2026
                </button>
              </li>
              {[
                ["For Professionals", "https://drive.google.com/drive/folders/1adaMUZKkC9-zfyWsOpE576_JnvZZL83P?usp=sharing"],
                ["Privacy Policy", "https://viewluxwindows.com/privacy-policy/"],
                ["Accessibility", "https://viewluxwindows.com/accessibility/"],
                ["Warranty", "https://viewluxwindows.com/warranty/"],
                ["Installation Info", "https://viewluxwindows.com/installation-information/"],
              ].map(([l, h]) => (
                <li key={l}>
                  <a
                    href={h}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-link-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-white/80 hover:text-[#e2b880] transition-colors"
                  >
                    {l}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-3">
              <SocialIcon
                href="https://www.facebook.com/profile.php?id=61589004344108"
                label="Facebook"
                Icon={Facebook}
              />
              <SocialIcon
                href="https://www.instagram.com/viewluxwindowscolorado"
                label="Instagram"
                Icon={Instagram}
              />
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#c99658]/15 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] tracking-[0.35em] uppercase font-raleway text-[#e2b880]/70">
          <p>© {new Date().getFullYear()} · ViewLux Colorado</p>
          <p>Precision · Refinement · Performance</p>
        </div>
      </div>
    </footer>
  );
}

function InfoRow({ icon: Icon, text, href }) {
  const isExternal = href && /^https?:/i.test(href);
  const Wrap = href ? "a" : "div";
  const extraProps = isExternal
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrap
      href={href}
      {...extraProps}
      className="flex items-center gap-4 text-white/80 text-[14px] hover:text-[#e2b880] transition-colors font-nunito"
    >
      <span className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-[#c99658]/30 text-[#e2b880] shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </span>
      {text}
    </Wrap>
  );
}

function SocialIcon({ href, label, Icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      data-testid={`social-${label.toLowerCase()}`}
      className="w-11 h-11 min-w-[44px] min-h-[44px] inline-flex items-center justify-center rounded-full border border-[#c99658]/30 text-[#e2b880] hover:bg-[#c99658] hover:text-[#0a1a17] hover:border-[#c99658] transition-colors"
    >
      <Icon className="w-4 h-4" />
    </a>
  );
}
